/**
 * A aba "Modelos prontos": os modelos que a pessoa pode montar, cada um com o
 * botão "Usar", que abre a configuração dele.
 *
 * O cartão segue o protótipo v2: no alto, a arte com os três passos do modelo
 * ligados por um fio; embaixo, a categoria, o nome, o que ele faz e o "Usar".
 * O que o protótipo inventava e o serviço não diz ficou de fora: "N min para
 * configurar", "Mais usado" e o filtro por categoria (cinco modelos não pedem
 * filtro). O "22 blocos para escolher" também: a conta certa é a da paleta do
 * editor, e um número aproximado aqui seria inventado.
 *
 * "Começar do zero" entra com o editor do mapa: sem ele, não há onde montar.
 * Ele não pergunta nada: cria o rascunho e abre o mapa, onde se escolhe como
 * começa (o evento ou a rotina) e se dá o nome.
 */

import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { montaAutomacaoInterna, type QuemNaTela } from '@/entities/acesso/papel';
import { chaves } from '@/shared/api/chaves';
import { useApi, useModelosDeAutomacao } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Icone, type NomeDoIcone } from '@/shared/ui';
import { Estado, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { iconeDoModelo } from '../lib/automacoes';
import {
  EVENTO_DE_QUEM_COMECA_DO_ZERO,
  arteDoModelo,
  configuracaoDoZero,
  modelosOferecidos,
  type Modelo,
} from '../lib/modelos';
import { ConfigurarModelo } from './configurar-modelo';
import './galeria-de-modelos.css';

export function GaleriaDeModelos({
  quem,
  aoCriado,
  aoCriadoDoZero,
}: {
  quem: QuemNaTela | null;
  aoCriado: (nome: string) => void;
  /** A automação criada do zero, para abrir no editor do mapa. */
  aoCriadoDoZero?: (id: string, nome: string) => void;
}): ReactNode {
  const modelos = useModelosDeAutomacao();
  const api = useApi();
  const cache = useQueryClient();
  const [usando, definirUsando] = useState<Modelo | null>(null);
  // Uma chave por clique: o duplo clique não cria duas, e um novo "Começar" cria outra.
  const doZero = useMutation({
    mutationFn: () =>
      api!.criarAutomacao({
        corpo: configuracaoDoZero(EVENTO_DE_QUEM_COMECA_DO_ZERO),
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (criada) => {
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      const nome = criada.nome ?? 'Automação sem nome';
      if (aoCriadoDoZero) aoCriadoDoZero(criada.id, nome);
      else aoCriado(nome);
    },
  });
  // Começar do zero cria uma automação interna: é de quem monta as internas.
  // Criar do zero é automação interna, que pede `automacao × criar`.
  const podeDoZero = montaAutomacaoInterna(quem, 'criar');

  return (
    <div className="galeria-de-modelos">
      <p className="galeria-de-modelos__intro">
        Escolha um ponto de partida. Você ajusta tudo antes de ligar, e nada roda sem alguém ativar.
      </p>

      <Estado consulta={modelos} forma="cartoes" linhas={3}>
        {(dados) => {
          const oferecidos = modelosOferecidos(dados.itens, quem);
          if (oferecidos.length === 0 && !podeDoZero) {
            return (
              <Vazio
                titulo="Nenhum modelo pronto para você ainda"
                porque="Os modelos que o seu papel monta aparecem aqui assim que o serviço das automações os oferecer."
              />
            );
          }
          return (
            <ul className="galeria-de-modelos__cartoes">
              {podeDoZero && (
                <li className="galeria-de-modelos__cartao" data-zero="sim" style={ordem(0)}>
                  <div className="galeria-de-modelos__arte" aria-hidden="true">
                    <No icone="play" rotulo="Início" indice={0} />
                    <span className="galeria-de-modelos__fio" />
                    <No icone="mais" rotulo="Seu bloco" indice={1} vazio />
                  </div>
                  <div className="galeria-de-modelos__corpo">
                    <span className="galeria-de-modelos__categoria">Em branco</span>
                    <h2 className="galeria-de-modelos__titulo">Começar do zero</h2>
                    <p className="galeria-de-modelos__texto">
                      Monte o mapa bloco a bloco: escolha como começa e ligue os próximos passos.
                    </p>
                  </div>
                  <div className="galeria-de-modelos__rodape">
                    <span className="galeria-de-modelos__meta">
                      <Icone nome="menu" />
                      Abre no mapa
                    </span>
                    <Botao
                      variante="secundario"
                      miudo
                      rotulo="Começar do zero"
                      onClick={() => doZero.mutate()}
                      desabilitado={doZero.isPending || api === null}
                      motivo={doZero.isPending ? 'Criando…' : 'Sem sessão: entre de novo.'}
                    >
                      <Icone nome="mais" />
                      {doZero.isPending ? 'Criando…' : 'Começar'}
                    </Botao>
                  </div>
                  {doZero.isError && (
                    <Faixa tom="bloqueado">
                      {recadoDeErro(doZero.error, {
                        generica: 'Não foi possível criar agora. Tente de novo.',
                        porCodigo: {
                          ENGINE_INDISPONIVEL:
                            'O serviço das automações não respondeu agora. Tente de novo daqui a pouco.',
                        },
                      })}
                    </Faixa>
                  )}
                </li>
              )}
              {oferecidos.map((m, i) => {
                const arte = arteDoModelo(m.chave);
                return (
                  <li
                    key={m.chave}
                    className="galeria-de-modelos__cartao"
                    style={ordem(i + (podeDoZero ? 1 : 0))}
                  >
                    <div className="galeria-de-modelos__arte" aria-hidden="true">
                      {arte ? (
                        arte.passos.map((p, j) => (
                          <Fragment key={p.rotulo}>
                            {j > 0 && <span className="galeria-de-modelos__fio" />}
                            <No icone={p.icone} rotulo={p.rotulo} indice={j} />
                          </Fragment>
                        ))
                      ) : (
                        <No icone={iconeDoModelo(m.chave)} rotulo="" indice={0} />
                      )}
                    </div>
                    <div className="galeria-de-modelos__corpo">
                      {arte && (
                        <span className="galeria-de-modelos__categoria">{arte.categoria}</span>
                      )}
                      <h2 className="galeria-de-modelos__titulo">{m.nome}</h2>
                      <p className="galeria-de-modelos__texto">{m.descricao}</p>
                    </div>
                    <div className="galeria-de-modelos__rodape">
                      {m.usa_sql && (
                        <span className="galeria-de-modelos__marca">Lê o banco da empresa</span>
                      )}
                      <Botao
                        variante="primario"
                        miudo
                        rotulo={`Usar o modelo ${m.nome}`}
                        onClick={() => definirUsando(m)}
                      >
                        <Icone nome="mais" />
                        Usar
                      </Botao>
                    </div>
                  </li>
                );
              })}
            </ul>
          );
        }}
      </Estado>

      {usando && (
        <ConfigurarModelo
          modelo={usando}
          aoFechar={() => definirUsando(null)}
          aoCriado={(nome) => {
            definirUsando(null);
            aoCriado(nome);
          }}
        />
      )}
    </div>
  );
}

/** Um passo da arte: o ladrilho do ícone e o rótulo embaixo. */
function No({
  icone,
  rotulo,
  indice,
  vazio = false,
}: {
  icone: NomeDoIcone;
  rotulo: string;
  indice: number;
  vazio?: boolean;
}): ReactNode {
  return (
    <span
      className="galeria-de-modelos__no"
      data-vazio={vazio ? 'sim' : undefined}
      style={{ '--j': indice } as CSSProperties}
    >
      <span className="galeria-de-modelos__no-icone">
        <Icone nome={icone} />
      </span>
      {rotulo}
    </span>
  );
}

/** A posição do cartão na entrada escalonada; acima do sexto, entram juntos. */
function ordem(i: number): CSSProperties {
  return { '--i': Math.min(i, 6) } as CSSProperties;
}
